import { useEffect, useRef } from 'react'
import Collapse from 'react-bootstrap/Collapse'
import Form from 'react-bootstrap/Form'
import { useSignal } from '@preact/signals-react'

function TriStateCheckbox({ checked, indeterminate, onChange, label, className }) {
    const ref = useRef(null)
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate
    }, [indeterminate])
    return (
        <Form.Check
            ref={ref}
            type="checkbox"
            label={label}
            checked={checked}
            onChange={onChange}
            className={className}
        />
    )
}

function CollapseArrow({ isCollapsed, onClick }) {
    return (
        <span
            onClick={e => { e.preventDefault(); e.stopPropagation(); onClick() }}
            style={{
                display: 'inline-block',
                width: '1rem',
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: 'bold',
                fontSize: '0.7rem',
                verticalAlign: 'middle',
                marginRight: '2px',
                color: '#000',
            }}
        >
            {isCollapsed ? '▶' : '▼'}
        </span>
    )
}

const EXCLUDED_SECTIONS = new Set(['apiInterfaceConfig', 'deviceConfig'])

const SECTION_LABELS = {
    utgGlobal: 'UTG Global',
    coreGlobal: 'Core Global',
    proactorSupervisor: 'Proactor Supervisor',
    expressManager: 'Express Manager',
    mailServer: 'Mail Server',
    webServer: 'Web Server',
}

export default function ConfigTemplateFieldChecklist({ schema, checkedFieldsSignal }) {

    const collapsedSignal = useSignal({})

    if (!schema?.properties?.config?.properties) return null

    const checkedPaths = checkedFieldsSignal.value
    const sectionMap = schema.properties.config.properties

    const toggleSection = (key) => {
        collapsedSignal.value = { ...collapsedSignal.value, [key]: !collapsedSignal.value[key] }
    }

    const handleFieldToggle = (dotPath, isChecked) => {
        const next = new Set(checkedPaths)
        if (isChecked) next.add(dotPath)
        else next.delete(dotPath)
        checkedFieldsSignal.value = next
    }

    const handleGroupToggle = (paths, isChecked) => {
        const next = new Set(checkedPaths)
        for (const p of paths) {
            if (isChecked) next.add(p)
            else next.delete(p)
        }
        checkedFieldsSignal.value = next
    }

    return (
        <div className="config-template-checklist" style={{ fontSize: '0.85rem' }}>
            {Object.entries(sectionMap).map(([sectionKey, section]) => {
                if (!section?.properties) return null
                if (EXCLUDED_SECTIONS.has(sectionKey)) return null

                const sectionPaths = collectStandardPaths(sectionKey, section)
                if (sectionPaths.length === 0) return null

                const sectionCheckedCount = sectionPaths.filter(p => checkedPaths.has(p)).length
                const sectionAllChecked = sectionCheckedCount === sectionPaths.length
                const sectionIndeterminate = sectionCheckedCount > 0 && !sectionAllChecked
                const isCollapsed = !!collapsedSignal.value[sectionKey]

                return (
                    <div key={sectionKey} className="mb-3">
                        <div className="d-flex align-items-center">
                            <TriStateCheckbox
                                checked={sectionAllChecked}
                                indeterminate={sectionIndeterminate}
                                label={null}
                                onChange={e => handleGroupToggle(sectionPaths, e.target.checked)}
                                className="mb-0 me-1"
                            />
                            <CollapseArrow isCollapsed={isCollapsed} onClick={() => toggleSection(sectionKey)} />
                            <strong style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSection(sectionKey)}>
                                {SECTION_LABELS[sectionKey] || sectionKey}
                            </strong>
                        </div>
                        <Collapse in={!isCollapsed}>
                            <div style={{ paddingLeft: '1.5rem' }}>
                                {renderStandardGroups(sectionKey, section, checkedPaths, handleFieldToggle, handleGroupToggle)}
                            </div>
                        </Collapse>
                    </div>
                )
            })}
        </div>
    )
}

// --- Standard sections ---

function collectStandardPaths(sectionKey, section) {
    const paths = []
    for (const [subKey, sub] of Object.entries(section.properties)) {
        if (sub?.type === 'object' && sub.properties) {
            for (const [fieldKey, field] of Object.entries(sub.properties)) {
                if (field['s4-display'] === false) continue
                paths.push(`${sectionKey}.${subKey}.${fieldKey}`)
            }
        } else {
            if (sub['s4-display'] === false) continue
            paths.push(`${sectionKey}.${subKey}`)
        }
    }
    return paths
}

function renderStandardGroups(sectionKey, section, checkedPaths, handleFieldToggle, handleGroupToggle) {
    const groups = buildStandardGroups(sectionKey, section)
    return groups.map(group => {
        if (group.paths.length === 0) return null
        const groupCheckedCount = group.paths.filter(p => checkedPaths.has(p)).length
        const groupAllChecked = groupCheckedCount === group.paths.length
        const groupIndeterminate = groupCheckedCount > 0 && !groupAllChecked
        return (
            <div key={group.id} className="mb-2">
                {group.label && (
                    <GroupCheckbox
                        checked={groupAllChecked}
                        indeterminate={groupIndeterminate}
                        label={<em>{group.label}</em>}
                        onChange={e => handleGroupToggle(group.paths, e.target.checked)}
                        className="mb-1"
                    />
                )}
                <div style={{ paddingLeft: group.label ? '1rem' : 0 }}>
                    {group.fields.map(({ dotPath, caption }) => (
                        <Form.Check
                            key={dotPath}
                            type="checkbox"
                            label={caption}
                            checked={checkedPaths.has(dotPath)}
                            onChange={e => handleFieldToggle(dotPath, e.target.checked)}
                            className="mb-0"
                        />
                    ))}
                </div>
            </div>
        )
    })
}

function GroupCheckbox({ checked, indeterminate, label, onChange, className }) {
    const ref = useRef(null)
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate
    }, [indeterminate])
    return (
        <Form.Check
            ref={ref}
            type="checkbox"
            label={label}
            checked={checked}
            onChange={onChange}
            className={className}
        />
    )
}

function buildStandardGroups(sectionKey, section) {
    const s4Groups = section['s4-groups'] || []
    const groupMap = {}
    for (const g of s4Groups) {
        groupMap[g.id] = { id: g.id, label: g.displayName, paths: [], fields: [] }
    }
    groupMap['__ungrouped__'] = { id: '__ungrouped__', label: null, paths: [], fields: [] }

    for (const [subKey, sub] of Object.entries(section.properties)) {
        if (sub?.type === 'object' && sub.properties) {
            for (const [fieldKey, field] of Object.entries(sub.properties)) {
                if (field['s4-display'] === false) continue
                const dotPath = `${sectionKey}.${subKey}.${fieldKey}`
                const caption = field['s4-caption'] || fieldKey
                const groupId = field['s4-group-id'] || '__ungrouped__'
                const bucket = groupMap[groupId] || groupMap['__ungrouped__']
                bucket.paths.push(dotPath)
                bucket.fields.push({ dotPath, caption })
            }
        } else {
            if (sub['s4-display'] === false) continue
            const dotPath = `${sectionKey}.${subKey}`
            const caption = sub['s4-caption'] || subKey
            const groupId = sub['s4-group-id'] || '__ungrouped__'
            const bucket = groupMap[groupId] || groupMap['__ungrouped__']
            bucket.paths.push(dotPath)
            bucket.fields.push({ dotPath, caption })
        }
    }

    const result = s4Groups.map(g => groupMap[g.id]).filter(g => g.fields.length > 0)
    if (groupMap['__ungrouped__'].fields.length > 0) result.push(groupMap['__ungrouped__'])
    return result
}
